import { Controller, useFormContext } from "react-hook-form";

import ComboBox from "ds/components/ComboBox";
import ComboBoxDefaultItem from "ds/components/ComboBox/DefaultItem";
import FormField from "ds/components/Form/Field";
import FormToggleField from "ds/components/Form/ToggleField";
import Input from "ds/components/Input";
import Textarea from "ds/components/Textarea";
import { BlueprintInput, BlueprintInputType } from "types/generated";
import FormFieldSpace from "components/FormFields/Space";

import useDeploymentAvailableSpaces from "./useDeploymentAvailableSpaces";
import { buildValidationRules } from "./helpers";
import DeploymentFormSecretField from "./SecretField";
import { SecretFieldsState, VersionTemplateFormFields } from "./types";

type DeploymentFormFieldProps = {
  input: BlueprintInput;
  editMode?: boolean;
  secretFieldsState?: SecretFieldsState;
};

const DeploymentFormField = ({ input, editMode, secretFieldsState }: DeploymentFormFieldProps) => {
  const { register, control, formState } = useFormContext<VersionTemplateFormFields>();
  const validationRules = buildValidationRules(input.validations);
  const isOptional = !input.validations?.required;
  const error = formState.errors.inputs?.[input.id];
  const availableSpaces = useDeploymentAvailableSpaces(editMode);

  switch (input.type) {
    case BlueprintInputType.ShortText: {
      return (
        <FormField
          key={input.id}
          label={input.name}
          helperText={input.description}
          noMargin
          isOptional={isOptional}
          error={error?.message}
        >
          {({ ariaInputProps }) => (
            <Input
              {...register(`inputs.${input.id}`, validationRules)}
              {...ariaInputProps}
              error={!!error}
            />
          )}
        </FormField>
      );
    }
    case BlueprintInputType.LongText: {
      return (
        <FormField
          key={input.id}
          label={input.name}
          helperText={input.description}
          noMargin
          isOptional={isOptional}
          error={error?.message}
        >
          {({ ariaInputProps }) => (
            <Textarea
              {...register(`inputs.${input.id}`, validationRules)}
              {...ariaInputProps}
              error={!!error}
            />
          )}
        </FormField>
      );
    }
    case BlueprintInputType.Secret: {
      return (
        <DeploymentFormSecretField
          input={input}
          editMode={editMode}
          secretFieldsState={secretFieldsState}
        />
      );
    }
    case BlueprintInputType.Number: {
      return (
        <FormField
          key={input.id}
          label={input.name}
          helperText={input.description}
          noMargin
          isOptional={isOptional}
          error={error?.message}
        >
          {({ ariaInputProps }) => (
            <Input
              type="number"
              step={input.validations?.step ?? undefined}
              {...register(`inputs.${input.id}`, validationRules)}
              {...ariaInputProps}
              error={!!error}
            />
          )}
        </FormField>
      );
    }
    case BlueprintInputType.Select: {
      return (
        <Controller
          name={`inputs.${input.id}`}
          control={control}
          rules={validationRules}
          render={({ field }) => (
            <ComboBox
              key={input.id}
              label={input.name}
              helperText={input.description}
              items={input.options.map((option) => ({ value: option, label: option }))}
              onChange={field.onChange}
              value={field.value as string}
            >
              {(item) => <ComboBoxDefaultItem id={item.value} label={item.label} />}
            </ComboBox>
          )}
        />
      );
    }
    case BlueprintInputType.Float: {
      return (
        <FormField
          key={input.id}
          label={input.name}
          helperText={input.description}
          noMargin
          isOptional={isOptional}
          error={error?.message}
        >
          {({ ariaInputProps }) => (
            <Input
              type="number"
              step={input.validations?.step ?? "any"}
              {...register(`inputs.${input.id}`, validationRules)}
              {...ariaInputProps}
              error={!!error}
            />
          )}
        </FormField>
      );
    }
    case BlueprintInputType.Boolean: {
      return (
        <Controller
          name={`inputs.${input.id}`}
          control={control}
          render={({ field }) => (
            <FormToggleField
              variant="switch"
              id={`inputs.${input.id}`}
              onChange={field.onChange}
              checked={field.value as boolean}
              aria-label={
                field.value
                  ? `Unselect ${input.description || input.name}`
                  : `Select ${input.description || input.name}`
              }
              title={input.name}
              description={input.description}
            />
          )}
        />
      );
    }
    case BlueprintInputType.AvailableSpaces: {
      return <FormFieldSpace key={input.id} spaces={availableSpaces} />;
    }
    default: {
      return (
        <FormField
          key={input.id}
          label={input.name}
          helperText={input.description}
          noMargin
          isOptional={isOptional}
          error={error?.message}
        >
          {({ ariaInputProps }) => (
            <Input
              {...register(`inputs.${input.id}`, validationRules)}
              {...ariaInputProps}
              error={!!error}
            />
          )}
        </FormField>
      );
    }
  }
};

export default DeploymentFormField;
